<script setup lang="ts">
import UiChip from '@boats/ui/components/UiChip.vue'
import { fetchProductsData, fetchRelatedData, type InfoData } from '~/queries'
import { queryKey } from '~/queries/helpers'
import { cleanPath } from '~/utils'
import KeyLabelFeature from '~/components/KeyLabelFeature.vue'
import ViewBanner from '~/components/football/ViewBanner.vue'
import dayjs from "~/plugins/dayjs";

const props = defineProps<{
  breadcrumbs: InfoData['breadcrumbs']
  page: InfoData['page']
  refresh: () => Promise<unknown>
}>()

const favourites = useFavouritesStore()
const route = useRoute()

// Storefront view (BOAT-1912): a club page may run on this generic category
// template too, so `?view=<slug>` filters the products, swaps in the partner
// logo and carries the param into every product link. URL only.
const { activeView, offeredViews, viewQuery, hrefForView, exitHref } = useStorefrontView(() => props.page?.views)

function getProductsPerPage() {
  if (props?.page?.templateAlias === 'predefined_search_page') {
    return 30
  } else {
    return props.page?.hasChild ? 30: 100
  }
}
const productsPerPage = getProductsPerPage()
const currentPage = computed(() => parseInt((route.query.page || 1) as string))
const offset = computed(() => (currentPage.value - 1) * productsPerPage)
usePaginationCanonical(currentPage.value)

const productsFilters = computed(() => {
  if (!props.page) return

  const defaultFilters: Record<string, unknown> = {
    'search[current]': 1,
  }

  if (props.page.templateAlias === 'predefined_search_page' && props.page.predefinedSearchCriteriaOptionId) {
    defaultFilters['search[tag_filter]'] = props.page.predefinedSearchCriteriaOptionId;
  } else {
    defaultFilters['search[page-descendants]'] = props.page.id;
  }
  if (activeView.value) {
    defaultFilters['search[view]'] = activeView.value.slug
  }

  return defaultFilters
})

const authToken = useCookie('authToken')



const [productsResult, relatedResult] = await Promise.all([
  useAsyncData(
    queryKey('products', productsPerPage, offset.value, productsFilters.value),
    async () => {
      if (!productsPerPage || !productsFilters.value) return

      const data = await fetchProductsData({
        limit: productsPerPage,
        offset: offset.value,
        ...productsFilters.value
      })

      if ((!data || data.items.length === 0) && route.query.page) {
        throw createError({ statusCode: 404, statusMessage: 'Not Found' })
      }

      return data;
    },
    { watch: [authToken, offset, productsFilters] }
  ),
  useAsyncData(
    queryKey('related', props.page?.id),
    async () => {
      if (!props.page?.id) return

      return await fetchRelatedData({id: props.page.id})
    },
    { watch: [authToken] }
  )
])

if (productsResult.error.value?.statusCode === 404) {
  throw productsResult.error.value
}

const {
  data: productsData,
  pending: productsPending,
  refresh: productsRefresh
} = productsResult

const { data: relatedData, refresh: relatedRefresh } = relatedResult

const breadcrumbsItems = computed(() =>
  props.breadcrumbs.map((item) => ({
    title: item.title,
    href: cleanPath('/', item.path)
  }))
)

const products = computed(() => productsData.value?.items)
const totalProducts = computed(() => productsData.value?.total)

const pagination = computed(() => {
  const totalPages = productsData.value?.total
    ? Math.ceil(productsData.value?.total / productsPerPage)
    : 1

  const previousPage = Math.max(currentPage.value - 1, 1)
  const nextPage = Math.min(currentPage.value + 1, totalPages)

  const path = cleanPath(route.path)
  // Keep the active storefront view across pages of the list.
  const viewParam = activeView.value ? `view=${encodeURIComponent(activeView.value.slug)}` : ''
  const withQuery = (params: string[]) => {
    const query = params.filter(Boolean).join('&')
    return query ? `${path}?${query}` : path
  }

  const previousHref = withQuery([previousPage > 1 ? `page=${previousPage}` : '', viewParam])
  const nextHref = withQuery([`page=${nextPage}`, viewParam])

  return {
    current: currentPage.value,
    total: totalPages,
    previousHref,
    nextHref
  }
})

const related = computed(() => relatedData.value?.items)

const scrollToProducts = () => {
  const top = (document.getElementById('#products')?.offsetTop || 80) - 80

  window.scrollTo({ top: top || 0, behavior: 'smooth' })
}

const productDates = (product) => {
  const details = [];
  switch (product.type) {
    case 'football_match':
    case 'event':
      if (product.startDate && product.startTime) {
        details.push(formatDate(product.startDate))
        details.push(formatTime([product.startDate, product.startTime].join(' ')))
      }
      else if (product.startDate) {
        details.push(formatDate(product.startDate))
      }
      break;
    default:
      if (product.startDate && dayjs.utc(product.startDate).isAfter(dayjs().utc())) {
        details.push(`Opens ${formatDate(product.startDate)}`)
      }
      else if (product.endDate && dayjs.utc(product.endDate).isAfter(dayjs().utc())) {
        details.push(`Booking until ${formatDate(product.endDate)}`)
      }
  }

  details.push(product.venue.name)

  return details.filter(Boolean).join(', ');
}

</script>

<template>
  <AppContainer v-if="page" class="my-10 md:my-20">
    <header class="row justify-between lg:flex-row-reverse">
      <div class="col-lg-6 mb-3 md:mb-6 lg:mb-0">
        <AppCarouselMedia :object="page" type="page" />
      </div>

      <div class="col-lg-5">
        <div class="hidden md:block">
          <AppBreadcrumbs class="!mb-3" :items="breadcrumbsItems" />
        </div>

        <h1 class="mb-2 md:mb-3">{{ page.title }}</h1>
        <img
          v-if="activeView?.logoUrl"
          :src="activeView.logoUrl"
          :alt="activeView.name || ''"
          class="category-partner-logo"
        />

        <AppPreviewContent>
          <div
            v-if="page.description"
            class="lead clean-cms-styles"
            v-html="page.description"
          ></div>
        </AppPreviewContent>
      </div>
    </header>

    <!-- Storefront views (BOAT-1912): offer each available view on the default
         page; offer the way back while one is active. -->
    <div v-if="offeredViews.length || activeView" class="category-view-banners">
      <ViewBanner
        v-for="view in offeredViews"
        :key="view.slug"
        :title="view.bannerTitle"
        :body="view.bannerBody"
        :cta="view.bannerCta || view.name || view.slug"
        :colour="view.colour"
        :href="hrefForView(view)"
      />
      <ViewBanner
        v-if="activeView"
        exit
        :title="activeView.name"
        :cta="$t('string.View-All-Fixtures')"
        :href="exitHref"
      />
    </div>

    <AppDivider id="#products" class="mb-4 mt-8 md:my-16" strong />

    <section class="row">
      <div class="col-lg-3 mb-6 lg:mb-0">
        <h2 class="alt">
          {{ $t('page.category.products.title') }}
          {{ totalProducts && totalProducts > 0 ? `(${totalProducts})` : '' }}
        </h2>
      </div>

      <div class="col-lg-9">
        <template v-if="products?.length">
          <div
            v-for="product in products"
            :key="product.id"
            :class="{
              'pointer-events-none opacity-25 transition-opacity':
                productsPending,
            }"
          >
            <AppCardInline
              :id="product.id"
              type="product"
              :to="cleanPath('/', product.path) + viewQuery"
              :title="product.title"
              :image="{ src: product.image, alt: product.title }"
              :favourited="product.favourited"
              :refetch="productsRefresh"
            >
              <template v-if="product.type === 'football_match' && product?.competitions?.[0]?.name">
                <p>{{ product.competitions[0].name }}</p><br>
              </template>
              <div class="flex items-center justify-between w-full">
                <p>{{ productDates(product) }}</p>
                <p v-if="product.startPrice">
                  {{ $t('string.Tickets-from') }}
                  <UiChip variant="price">
                    {{ formatCurrency(product.startPrice) }}
                  </UiChip>
                </p>
              </div>
              <div
                v-if="product.keyFeatureLabels?.length"
                class="py-2 flex flex-wrap"
              >
                <div v-if="product.keyFeatureLabels?.length" class="mt-2">
                  <KeyLabelFeature
                    v-for="(label, i) in product.keyFeatureLabels"
                    :key="'key-label-feature' + i + product.id"
                    :name="label.name"
                    :colour="label.colour"
                    class="mr-2"
                  />
                </div>
              </div>
            </AppCardInline>

            <AppDivider class="my-4 md:my-6" />
          </div>

          <AppPagination
            v-if="pagination.total > 1"
            class="mt-8"
            :current="pagination.current"
            :total="pagination.total"
            :previous-href="pagination.previousHref"
            :next-href="pagination.nextHref"
            :previous-disabled="pagination.current <= 1"
            :next-disabled="pagination.current >= pagination.total"
            @previous="scrollToProducts"
            @next="scrollToProducts"
          />
        </template>

        <AddSadMessage v-else>
          <template #title>
            {{ $t('page.category.no-products.title') }}
          </template>

          <p>{{ $t('page.category.no-products.description') }}</p>
        </AddSadMessage>
      </div>
    </section>

    <template v-if="related?.length">
      <AppDivider class="mb-4 mt-8 md:my-16" strong />

      <AppCarouselCard>
        <template #title>{{ $t('page.category.related.title') }}</template>

        <div v-for="item in related" :key="item.id" class="carousel-item">
          <AppCard
            :id="item.id"
            type="page"
            :to="cleanPath('/', item.path)"
            :title="item.title"
            :image="{ src: item.image, alt: item.title }"
            :favourited="item.favourited"
            :refetch="relatedRefresh"
          ></AppCard>
        </div>
      </AppCarouselCard>
    </template>

    <AppDivider class="my-8 md:my-16" strong />

    <AppNewsletter />
  </AppContainer>
</template>

<style scoped lang="scss">
/* Storefront view (BOAT-1912): partner lock-up under the H1, generous size. */
.category-partner-logo {
  display: block;
  max-height: 96px;
  max-width: min(100%, 320px);
  margin: 4px 0 20px;
  object-fit: contain;

  @media (min-width: 768px) {
    max-height: 120px;
    max-width: min(100%, 400px);
    margin: 8px 0 24px;
  }
}
.category-view-banners {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}
</style>
